<template>
  <div class="assetDetailEngagement"
    v-loading="metricDataLoading"
  >
    <div class="assetDetailEngagement-title">
      <h3>ENGAGEMENT</h3>
    </div>
    <div class="assetDetailEngagement-content">
        <div class="assetDetailEngagement-content-chart">
          <div id="engagementChart"></div>
        </div>
        <div class="assetDetailEngagement-content-metrics">
          <div>
            <neb-metric :value="avgCompletionRate"
              title="Avg. Completion Rate"
            />
          </div>
          <div>
            <neb-metric :value="scrollInitiationRate"
              title="Scroll Initiation Rate"
            />
          </div>
          <div>
            <neb-metric :value="avgScrollDepth"
              title="Avg. Scroll Depth"
            />
          </div>
          <div>
            <neb-tooltip :content="humanSumTimeOnContent">
              <neb-metric :value="sumTimeOnContent"
                title="Total Time on Content"
              />
            </neb-tooltip>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { useHorizontalBarChart } from '@/features'
import { onMounted, ref, computed, watch } from '@vue/composition-api'
import client from '@/api/client'
import NebMetric from '@/components/Nebula/visualization/Metric'
import { formatPercent, formatDuration } from '@/components/Nebula/formatting.js'
import NebTooltip from '@/components/Nebula/generic/Tooltip'

const chartColors = [
  '#facc46', '#ffa407'
]

export default {
  name: 'AssetDetailEngagement',

  components: {
    NebMetric,
    NebTooltip
  },

  props: {
    brandIdProp: {
      type: [ Number, String ],
      required: true
    },
    pageUrlId: {
      type: String,
      required: true
    },
    metricDateRange: {
      type: Array,
      required: true
    }
  },

  setup (props) {
    const metricData = ref({})
    const metricDataLoading = ref(false)
    const chart = useHorizontalBarChart('engagementChart', {
      width: 400,
      height: 80,
      config: {
        style: {
          cell: {
            stroke: 'transparent'
          }
        }
      },
      transform: [
        {
          calculate: 'join([format(floor(datum.value / 60), "02"), format(floor(datum.value % 60), "02")], ":")',
          field: 'value', as:
          'humanizedValue'
        }
      ],
      layer: [
        {
          mark: {
            type: 'bar',
            height: 20
          }
        },
        {
          mark: {
            type: 'text',
            align: 'left',
            baseline: 'middle',
            dx: 7,
            font: 'Helvetica Neue',
            fontWeight: 700,
            fontSize: 20
          },
          encoding: {
            text: { field: 'humanizedValue', type: 'nominal' },
            color: { value: '#5c5e62' },
          }
        }
      ],
      encoding: {
        x: {
          axis: {
            grid: false,
            labels: false,
            ticks: false,
            title: null,
            domain: false
          }
        },
        y: {
          axis: {
            grid: false,
            ticks: false,
            title: null,
            domain: false,
            labelColor: '#5c5e62',
            labelFont: 'Helvetica Neue',
            labelFontSize: 12,
            labelFontWeight: 300,
            labelPadding: 10
          },
          sort: [ 'Duration', 'Avg. Time on Content' ],
          scale: {
            padding: 2
          }
        },
        color: {
          field: 'category',
          scale: {
            range: chartColors
          },
          legend: null
        }
      }
    })
    const chartData = computed(() => [
      { category: 'Duration', value: metricData.value.AVG__DURATION || 0 },
      { category: 'Avg. Time on Content', value: metricData.value.AVG__AVG_TIME_ON_CONTENT || 0 }
    ])
    const getData = async () => {
      const params = {
        metrics: [
          { metric: 'avg_time_on_content', agg: 'avg' },
          { metric: 'duration', agg: 'avg' },
          { metric: 'completion_rate', agg: 'avg' },
          { metric: 'scroll_initiation_rate', agg: 'avg' },
          { metric: 'avg_scroll_depth', agg: 'avg' },
          { metric: 'time_on_content', agg: 'sum' }
        ],
        breakdown: 'page_url_id',
        brand_ids: [ props.brandIdProp ],
        page_url_ids: [ props.pageUrlId ],
        metric_date_range: {
          gte: props.metricDateRange[0],
          lte: props.metricDateRange[1]
        }
      }

      const resp = await client.metrics(params)

      return resp.data.results[0] || {}
    }
    const loadData = async () => {
      metricDataLoading.value = true
      const data = await getData()
      metricData.value = data
      metricDataLoading.value = false
    }

    const loadDataAndRenderChart = async () => {
      await loadData()
      chart.setData(chartData.value)
      await chart.render()
    }

    watch(() => props.metricDateRange, loadDataAndRenderChart)

    onMounted(loadDataAndRenderChart)

    return {
      chartData,
      avgCompletionRate: computed(() => formatPercent(metricData.value.AVG__COMPLETION_RATE || 0)),
      scrollInitiationRate: computed(() => formatPercent(metricData.value.AVG__SCROLL_INITIATION_RATE || 0)),
      avgScrollDepth: computed(() => formatPercent(metricData.value.AVG__AVG_SCROLL_DEPTH || 0)),
      sumTimeOnContent: computed(() => formatDuration(metricData.value.SUM__TIME_ON_CONTENT || 0)),
      humanSumTimeOnContent: computed(() => formatDuration(metricData.value.SUM__TIME_ON_CONTENT || 0, true)),
      metricDataLoading
    }
  }
}
</script>

<style lang="scss">
@import '../Nebula/style.scss';

.assetDetailEngagement {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &-title {
    h3 {
      margin: 0;
      font-family: $defaultFont;
      font-size: 14px;
      color: $slate-grey;
      font-weight: 300;
    }
  }

  &-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &-chart {
      flex-basis: 50%;
    }

    &-metrics {
      display: flex;
      align-items: center;
      justify-content: space-around;
      flex-basis: 50%;
    }
  }
}
</style>
